/**
 * Оценка и фидбек куратора за урок (D11, 10.09.2026).
 *
 * ⚠️ ЗАЧЕМ РАЗБОР ОТДЕЛЬНО ОТ ЭКРАНОВ. Поле `curator_grade` пишет сервер
 * параллельной сессии, и на стенде его может ещё не быть. Экраны обязаны:
 * отличать «оценки нет» от «сервер старый» (тогда кураторка работает как
 * раньше), не превращать мусор в оценку и не показывать ученику «0/100» там,
 * где оценки просто не ставили. Ноль при этом — настоящая оценка.
 */

import { apiErrorCode } from './apiErrorCode';
import { lessonGradeRaw } from './studentFields';

export type CuratorGrade = {
  /** 0–100; `null` — оценки нет. Ноль — настоящая оценка. */
  grade: number | null;
  /** Текст куратора; пустая строка считается отсутствием. */
  feedback: string | null;
  updated_at: string | null;
};

export type LessonGradeHistoryItem = {
  grade: number | null;
  feedback: string | null;
  changed_by: { id: string; name: string } | null;
  changed_at: string;
};

const isGrade = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 100;

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** `curator_grade` из ответа сервера → оценка или `null`, если нечего показать. */
export function parseCuratorGrade(raw: unknown): CuratorGrade | null {
  if (!isObject(raw)) return null;
  const grade = isGrade(raw.grade) ? raw.grade : null;
  const feedback = typeof raw.feedback === 'string' && raw.feedback.trim() !== '' ? raw.feedback : null;
  if (grade === null && feedback === null) return null;
  return { grade, feedback, updated_at: typeof raw.updated_at === 'string' ? raw.updated_at : null };
}

/**
 * Прислал ли сервер поле вообще. `null` в поле — это «оценки нет», а
 * отсутствие поля — «сервер ещё не умеет фидбек»: тогда кураторка остаётся
 * прежней, с одним полем оценки.
 */
export function hasCuratorGradeField(row: object): boolean {
  return Object.prototype.hasOwnProperty.call(row, 'curator_grade');
}

/**
 * Что стоит под строкой урока в «Прогрессе ученика» — одно правило на компьютер и телефон (13.10.2026: у экрана
 * появилась телефонная ветка, и условия режима курса, записанные дважды, разошлись бы при первой правке).
 *
 * - `feedback` — оценивает куратор, сервер уже присылает `curator_grade`: оценка и фидбек одной формой (D11);
 * - `grade` — оценивает куратор, сервер старый, без поля: прежнее поле оценки;
 * - `openNext` — оценивает система (и мини-курс), и ученик стоит на этом уроке: «Открыть следующий». Строго `auto`,
 *   а не «не куратор»: старый сервер режима не присылает, и кнопка без ручки отказала бы 404;
 * - `null` — ничего. В режиме auto балл ставит система, и куратор его не правит.
 */
export type LessonRowAction = 'feedback' | 'grade' | 'openNext' | null;

export function lessonRowAction(mode: 'curator' | 'auto' | undefined, row: object, isStuck: boolean): LessonRowAction {
  if (mode === 'curator') return hasCuratorGradeField(row) ? 'feedback' : 'grade';
  if (mode === 'auto' && isStuck) return 'openNext';
  return null;
}

/** Текст чипа «90/100». Нет оценки — нет чипа. */
export function gradeChipText(g: CuratorGrade | null | undefined): string | null {
  return g && g.grade !== null ? `${g.grade}/100` : null;
}

/** Оценки из `GET /progress/marathons/{id}/my-progress` → карта «урок → оценка». */
export function curatorGradesByLesson(progress: unknown): Map<string, CuratorGrade> {
  const out = new Map<string, CuratorGrade>();
  const lessons = isObject(progress) ? progress.lessons : undefined;
  if (!Array.isArray(lessons)) return out;
  for (const item of lessons) {
    if (!isObject(item) || typeof item.lesson_id !== 'string' || !item.lesson_id) continue;
    // `grade`, раньше `curator_grade` (договор student-api.md §4).
    const g = parseCuratorGrade(lessonGradeRaw(item));
    if (g) out.set(item.lesson_id, g);
  }
  return out;
}

/** История правок: новые сверху, битые строки выброшены. */
export function parseGradeHistory(raw: unknown): LessonGradeHistoryItem[] {
  const list = Array.isArray(raw) ? raw : isObject(raw) && Array.isArray(raw.items) ? raw.items : [];
  const items: LessonGradeHistoryItem[] = [];
  for (const it of list) {
    if (!isObject(it) || typeof it.changed_at !== 'string') continue;
    const by = isObject(it.changed_by) ? it.changed_by : null;
    items.push({
      grade: isGrade(it.grade) ? it.grade : null,
      feedback: typeof it.feedback === 'string' && it.feedback.trim() !== '' ? it.feedback : null,
      changed_by:
        by && typeof by.id === 'string' ? { id: by.id, name: typeof by.name === 'string' ? by.name : '' } : null,
      changed_at: it.changed_at,
    });
  }
  return items.sort((a, b) => b.changed_at.localeCompare(a.changed_at));
}

/** 409 `grading_mode_auto`: курс перевели в автоматический режим, пока куратор смотрел. */
export function isGradingModeAutoError(err: unknown): boolean {
  // ⚠️ Через `apiErrorCode`: клиент API кладёт тело ответа в `raw`, а не в
  // `response.data`. До 10.09.2026 здесь читался `response.data.code`, и в живом
  // приложении отказ ни разу не узнавался.
  return apiErrorCode(err) === 'grading_mode_auto';
}
